// Progetti (tela, artboard 23): dove lavorano le sessioni, cioè lo spazio dei file e il git di
// riferimento con branch, push e pull request. Ognuno si apre e si crea su una pagina sua
// (`ProjectPage`, artboard 25).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import type { GitRegistry, Project } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { EmptyState } from '../../components/EmptyState';
import { PopHero } from '../../components/PopHero';
import { SkeletonRow } from '../../components/Skeleton';
import { useStore } from '../../store/StoreProvider';

const PUSH: Record<string, string> = {
  task: 'dopo ogni lavoro',
  mission: 'a fine missione',
  manual: 'solo a mano',
};
const PULL: Record<string, string> = { none: '', open: ', con PR', merge: ', con PR unita' };

const TINTS = ['mint', 'blue', 'lilac', 'coral', 'sun', 'pink'] as const;

// Un progetto come scheda della tela: iniziale colorata, nome e git, descrizione, poi lo spazio,
// il branch e il push in piccolo.
function ProjectCard({
  p,
  i,
  registry,
  sessions,
  onOpen,
}: {
  p: Project;
  i: number;
  registry: GitRegistry | undefined;
  sessions: number;
  onOpen: () => void;
}) {
  const rows: [string, string, boolean][] = [
    ['Spazio', `Locale · works/projects/${p.workspace.folder}`, false],
    ['Branch', `${p.baseBranch} → ${p.workBranch}`, true],
    [
      'Push',
      p.git
        ? PUSH[p.push] + (registry?.kind === 'git' ? '' : PULL[p.pullRequest])
        : 'solo commit locali',
      false,
    ],
  ];
  const failing = p.git && registry?.lastTest && !registry.lastTest.ok;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="project-card"
      data-tint={TINTS[i % TINTS.length]}
    >
      <span className="project-card-head">
        <span className="project-card-initial" aria-hidden>
          {p.name.trim().charAt(0).toUpperCase() || '?'}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="project-card-name trunc">{p.name}</span>
          <span className="project-card-repo trunc">
            {p.git ? `${registry?.name ?? 'registro tolto'} · ${p.git.repo}` : 'nessun git'}
          </span>
        </span>
        {p.default ? (
          <span className="project-card-pill">Predefinito</span>
        ) : (
          <span className="project-card-pill" data-muted>
            {sessions} {sessions === 1 ? 'sessione' : 'sessioni'}
          </span>
        )}
      </span>
      {p.description && <span className="project-card-desc">{p.description}</span>}
      <span className="project-card-rows">
        {rows.map(([k, v, mono]) => (
          <span key={k} style={{ display: 'contents' }}>
            <span className="faint">{k}</span>
            <span className="trunc" style={mono ? { fontFamily: 'var(--font-mono)' } : undefined}>
              {v}
            </span>
          </span>
        ))}
      </span>
      {failing && (
        <span className="t-caption" style={{ color: 'var(--danger)' }}>
          Il token di {registry.name} è rifiutato: il push è fermo.
        </span>
      )}
      <span className="project-card-foot">
        <span className="dash-more">Apri</span>
      </span>
    </button>
  );
}

export function Projects() {
  const { state } = useStore();
  const nav = useNavigate();
  const [rows, setRows] = useState<Project[] | null>(null);
  const [registries, setRegistries] = useState<GitRegistry[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.projects(), api.gitRegistries()]).then(
      ([p, g]) => {
        setRows(p.projects.filter((x) => !x.archived)); // gli archiviati stanno in Archivio
        setRegistries(g.registries);
      },
      (e: unknown) =>
        setError(e instanceof ApiError && e.detail ? e.detail : 'Non riesco a leggere i progetti.'),
    );
  }, []);

  const count = (id: string) => state.sessions.filter((s) => s.project === id).length;
  return (
    <div className="page">
      <PopHero
        title="Progetti"
        subtitle={
          rows?.length
            ? `${rows.length} ${rows.length === 1 ? 'cantiere aperto' : 'cantieri aperti'} in città: lo spazio dei file e il git di riferimento.`
            : 'Dove lavorano le sessioni: lo spazio dei file e il git di riferimento.'
        }
        actions={
          <button type="button" className="btn-sun" onClick={() => nav('/progetti/nuovo')}>
            <Icon name="plus" size={18} />
            Nuovo progetto
          </button>
        }
      />
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Banner kind="danger">{error}</Banner>
        </div>
      )}
      {rows?.length === 0 && (
        <EmptyState
          icon="folder"
          pop="team-lead-01"
          title="Nessun progetto"
          hint="Senza progetti ogni sessione lavora nel suo spazio locale. Un progetto dà ai pop una cartella e un git dove salvare il lavoro."
          action={
            <Button variant="primary" icon="plus" onClick={() => nav('/progetti/nuovo')}>
              Crea il primo progetto
            </Button>
          }
        />
      )}
      <div className="project-grid">
        {rows === null && !error && <SkeletonRow />}
        {rows?.map((p, i) => (
          <ProjectCard
            key={p.id}
            i={i}
            p={p}
            registry={registries.find((r) => r.id === p.git?.registry)}
            sessions={count(p.id)}
            onOpen={() => nav(`/progetti/${p.id}`)}
          />
        ))}
      </div>
    </div>
  );
}
